import { Component, OnInit, OnDestroy } from '@angular/core';
import { BookingStepService } from '../../services/booking-step.service';
import { AreaItemPack } from '../../models/area-item-pack';
import { BookingStepEnum } from '../../models/booking-step.enum';
import { AreaItem } from '../../models/area-item';
import { BookingOrderService } from '../../services/booking-order.service';
import { environment } from '../../../../../environments/environment';
import { Router } from '@angular/router';
import { ShopSettingsService } from '../../../general/services/shop-settings.service';
import { VisualItemInfo } from '../../../general/models/visual-item-info';
import { PaymentStateService } from '../../../payment/services/payment-state.service';
import { Subscription } from 'rxjs';
import { NavigationService } from '../../services/navigation.services';
import { PartnerCodeStateService } from 'src/app/modules/partner-code/services/partner-code-state.service';
import { HeaderTitleService } from 'src/app/modules/general/services/title.service';

@Component({
  selector: 'app-booking-packs-home',
  templateUrl: 'booking-packs-home.component.html',
  styleUrls: ['booking-packs-home.component.scss']
})
export class BookingPacksHomeComponent implements OnInit, OnDestroy {

  private visualItemsInfo = new Array<VisualItemInfo>();
  visualItemsInfoButtons = new Array<VisualItemInfo[]>();
  private paymentProvidersAvailable = false;
  private paymentProviderSubscriptions: Subscription;

  constructor(
    private router: Router,
    private bookingStepService: BookingStepService,
    private bookingOrderService: BookingOrderService,
    private shopSettingsService: ShopSettingsService,
    private paymentStateService: PaymentStateService,
    private navigationService: NavigationService,
    private partnerCodeStateService: PartnerCodeStateService,
    private headerTitleService: HeaderTitleService,
  ) { }

  ngOnInit(): void {
    this.bookingOrderService.areaItemPackGroupName = null;
    this.paymentProviderSubscriptions = this.paymentStateService.paymentProviders.subscribe(
      providers => {
        this.paymentProvidersAvailable = providers.length !== 0;
        this.initVisualItems();
        if (this.visualItemsInfo.length === 1 && this.skipSingleAreaItemPackSelection) {
          this.visualItemsInfo[0].action();
        }
      }
    );
  }

  ngOnDestroy(): void {
    if (this.paymentProviderSubscriptions) {
      this.paymentProviderSubscriptions.unsubscribe();
      this.paymentProviderSubscriptions = null;
    }
  }

  initVisualItems(): void {
    this.initBookingGroupButtons();

    if (this.showBookingButton) {
      this.visualItemsInfo.push(new VisualItemInfo(this.ticketButtonTitle, () => {
        this._updateTitle(this.ticketButtonTitle);
        this.navigateTo('booking');
      }));
    }

    if (this.showEventsButton) {
      this.visualItemsInfo.push(new VisualItemInfo(this.eventsButtonTitle, () => {
        this._updateTitle(this.eventsButtonTitle);
        this.navigateTo('booking/events');
      }));
    }

    if (this.showGiftsButton) {
      this.visualItemsInfo.push(new VisualItemInfo(this.giftCardsButtonTitle, () => {
        this._updateTitle(this.giftCardsButtonTitle);
        this.navigateTo('giftcard');
      }));
    }

    if (this.showProductsButton) {
      this.visualItemsInfo.push(new VisualItemInfo(this.productsButtonTitle, () => {
        this._updateTitle(this.productsButtonTitle);
        this.navigateTo('booking/products');
      }));
    }

    if (this.isShowCardInformationButton) {
      this.visualItemsInfo.push(new VisualItemInfo(this.cardInformationButtonTitle, () => {
        this.partnerCodeStateService.activateMode = false;
        this._updateTitle(this.cardInformationButtonTitle);
        this.navigateTo('partner-code');
      }));
    }

    if (this.isShowCardReplaceButton) {
      this.visualItemsInfo.push(new VisualItemInfo(this.cardReplaceButtonTitle, () => {
        this.partnerCodeStateService.activateMode = true;
        this._updateTitle(this.cardReplaceButtonTitle);
        this.navigateTo('partner-code');
      }));
    }

    if (this.isShowGuestCardRefund) {
      this.visualItemsInfo.push(new VisualItemInfo(this.guestCardRefundButtonTitle, () => {
        this._updateTitle(this.guestCardRefundButtonTitle);
        this.navigateTo('card-refund');
      }));
    }
    this.visualItemsInfoButtons = this.buildShownVisualItemsInfo();
  }

  initBookingGroupButtons(): void {
    for (const bookingGroupName of this.bookingGroupNames) {
      this.visualItemsInfo.push(new VisualItemInfo(bookingGroupName, () => { this.showBooking(bookingGroupName); }));
    }
  }

  get mainPageAreaItemPacks(): AreaItemPack[] {
    return this.bookingOrderService.mainPageAreaItemPacks;
  }

  get bookingGroupNames(): string[] {
    return this.bookingOrderService.bookingGroupNames;
  }

  get showBookingButton(): boolean {
    return this.navigationService.existBooking;
  }

  get showEventsButton(): boolean {
    return this.navigationService.existEvents;
  }

  get showGiftsButton(): boolean {
    return this.navigationService.existGifts && this.paymentForGiftCardAvailable && !this.shopSettingsService.isVUMode;
  }

  get showProductsButton(): boolean {
    return this.navigationService.existProducts;
  }

  get paymentForGiftCardAvailable(): boolean {
    return this.paymentProvidersAvailable && !this.shopSettingsService.isVUMode || this.navigationService.existCardInformation;
  }

  onAreaItemPackSelected(areaItemPack: AreaItemPack, selectedAreaItem: AreaItem = null): void {
    this.bookingStepService.update(
      BookingStepEnum.SelectBookingPacks,
      areaItemPack,
      selectedAreaItem
    );
  }

  navigateTo(url: string): void {
    this.router.navigateByUrl(url);
  }

  get srcVideo(): string {
    return environment.srcVideo;
  }

  get showNextDeparture(): boolean {
    return this.shopSettingsService.isVUMode;
  }

  get ticketButtonTitle(): string {
    return this.shopSettingsService.shopSettings.ticketsButtonTitle || 'Tickets';
  }

  get eventsButtonTitle(): string {
    return this.shopSettingsService.shopSettings.eventsButtonTitle || 'Events';
  }

  get giftCardsButtonTitle(): string {
    return this.shopSettingsService.shopSettings.giftCardsButtonTitle || 'Gift Cards';
  }

  get cardInformationButtonTitle(): string {
    return this.shopSettingsService.shopSettings.cardInformationButtonTitle || 'Card Information';
  }

  get cardReplaceButtonTitle(): string {
    return this.shopSettingsService.shopSettings.cardReplaceButtonTitle || 'Activate Card';
  }

  get productsButtonTitle(): string {
    return this.shopSettingsService.shopSettings.productsButtonTitle || 'Products';
  }

  get isShowCardInformationButton(): boolean {
    return this.navigationService.existCardInformation;
  }

  get isShowCardReplaceButton(): boolean {
    return this.navigationService.existCardInformation;
  }

  showBooking(groupName: string): void {
    this.bookingOrderService.areaItemPackGroupName = groupName;
    this._updateTitle(groupName);
    this.navigateTo('/booking');
  }

  get isShowGuestCardRefund(): boolean {
    return this.navigationService.existGuestCardRefund;
  }

  get guestCardRefundButtonTitle(): string {
    return 'Refund Guest Card';
  }

  public buildShownVisualItemsInfo(): VisualItemInfo[][] {
    let displayedVisualItemsInfo: VisualItemInfo[][];
    displayedVisualItemsInfo = [];
    if (!this.visualItemsInfo) {
      return displayedVisualItemsInfo;
    }

    let maxColumnCount = 3;

    if (this.visualItemsInfo.length === 2 || this.visualItemsInfo.length === 4) {
      maxColumnCount = 2;
    }

    let l = 0;
    let j = 0;
    displayedVisualItemsInfo[l] = [];
    for (const visualItem of this.visualItemsInfo) {
      if (j >= maxColumnCount) {
        l++; j = 0;
        displayedVisualItemsInfo[l] = [];
      }
      displayedVisualItemsInfo[l][j++] = visualItem;
    }
    this.beatifyDisplayedItems(displayedVisualItemsInfo);
    return displayedVisualItemsInfo;
  }

  rowCssClass(index: number): string {
    let cssClass = 'row visual-item-row-3';
    if (this.visualItemsInfo && this.visualItemsInfo.length !== index + 1) {
      cssClass = 'row visual-item-row-2';
    }
    return cssClass;
  }

  private beatifyDisplayedItems(displayedVisualItemsInfo: VisualItemInfo[][]): void {
    if (displayedVisualItemsInfo == null) {
      return;
    }

    for (const rows of displayedVisualItemsInfo) {
      let cssClass = ' visual-item-3';

      switch (rows.length) {
        case 2:
          cssClass = ' visual-item-2';
          break;
        case 3:
          cssClass = ' visual-item-3';
          break;
        default:
          break;
      }

      for (const row of rows) {
        row.cssClass = cssClass;
      }
    }
  }

  get skipSingleAreaItemPackSelection(): boolean {
    return this.shopSettingsService
      && this.shopSettingsService.shopSettings
      && this.shopSettingsService.shopSettings.skipSingleAreaItemPackSelection;
  }

  private _updateTitle(name: string): void {
    this.headerTitleService.title = name;
  }

}
